import { useCallback } from 'react';
import { CHART_TYPES, chartTypesAllowIncidents } from '../constants/chartTypes';
import KubitContextualMenu from '../../Dropdown/ContextualMenu';
import {
  ContextualMenuItemType,
  DirectionalHint,
  IContextualMenuItem,
} from '@fluentui/react';
import IncidentTypeSwitches from './IncidentTypeSwitches/IncidentTypeSwitches';
import {
  AnalysisStatus,
  CohortSampleOperationKey,
  PermissionKey,
  ScheduleType,
} from '../../../utils/apollo/graphqlSchema';
import { useShowBraze, useShowSql } from '../../../utils/hooks';
import {
  IDashboardDuplicateReportData,
  ModalSliceType,
  openModal,
} from '../../../redux/slices/modal';
import { useAppDispatch } from '../../../redux/hooks';
import { useCurrentUrl } from '../../../utils/routing';
import { RowType } from '../../../redux/slices/oldBuilder';
import { assertCondition, assertNotNil } from '../../../utils/types';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import toastMessage from '../../../utils/toastMessage';
import { useChartContext } from '../ChartContext';
import { isTransientAnalysisId } from '../../../redux/utils/transientAnalysis';
import useAnalysisSetChartPref, {
  usePrepareChartPrefs,
} from '../hooks/useAnalysisSetChartPref';
import ContextualMenuOption from '../../Dropdown/ContextualMenu/ContextualMenuOption';
import { SvgIcon } from '../../Icon';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { reportViewSql } from '../../../utils/analytics/events/report/events';

interface IProps {
  analysisId: string;
  isDisabled?: boolean;
}

const menuStyles = {
  list: {
    minWidth: '240px',
  },
};

const IconContainer = styled.button`
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  &:not(:disabled):hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
  }
  &[data-open='true'] {
    color: ${theme.colors.mainBlue};
  }
`;

export default ({ analysisId, isDisabled }: IProps) => {
  const dispatch = useAppDispatch();
  const showSql = useShowSql();
  const isBrazeConnect = useShowBraze();
  const { isDashboardInUrl, isWorkspacesInUrl } = useCurrentUrl();
  const { track } = useAnalytics();

  const isTransientAnalysis = isTransientAnalysisId(analysisId);

  const chartContext = useChartContext();
  assertCondition(chartContext.__typename === 'BaseChartContext');
  const {
    actions: { onDelete, rebuildChart },
    permissions: { entityPermissions, entityPermissionsHint },
    metadata,
    settings: { chartSettings },
  } = chartContext;
  const { display, description, status, query, queryType, isEmbedded } =
    metadata;
  const prepareChartPrefs = usePrepareChartPrefs(analysisId);
  const { chartType, singleDayLabel } = chartSettings;
  const { checkIsPermitted } = usePermission();

  const isPermittedToSaveCohortAndExportBraze = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CohortBrazeExportCreate,
      PermissionKey.CohortCreate,
    ],
  });
  const setAnalysisChartPref = useAnalysisSetChartPref(analysisId);

  const saveChartSettings = () => {
    const res = setAnalysisChartPref();
    res.then((errors) => {
      if (errors.length > 0) {
        toastMessage({ message: errors.join(', '), type: 'error' });
      } else {
        toastMessage({ message: 'Chart settings saved', type: 'info' });
      }
    });
  };

  const openDeleteFromWorkspaceModal = () => {
    dispatch(
      openModal({
        type: ModalSliceType.WorkspaceDeleteReport,
        id: analysisId,
      }),
    );
  };

  const openDuplicateReportModal = () => {
    const { pref } = prepareChartPrefs();
    dispatch(
      openModal({
        type: ModalSliceType.DashboardDuplicateReport,
        data: {
          analysisId,
          display,
          description,
          pref,
        } satisfies IDashboardDuplicateReportData,
      }),
    );
  };

  const openCreateAnalysisScheduleModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.CreateSchedule,
        id: `${ScheduleType.Analysis}-${analysisId}`,
      }),
    );
  }, [analysisId, dispatch]);

  const openRenameAnalysisModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.RenameReport,
        data: {
          title: 'Rename Report',
          queryType,
          display,
          description,
        },
        id: analysisId,
      }),
    );
  }, [analysisId, dispatch, queryType, display, description]);

  const handleSaveAndExportToBraze = useCallback(() => {
    assertCondition('cohort' in query);
    const cohortBuilderInfo = query.cohort;

    dispatch(
      openModal({
        type: ModalSliceType.SaveCohortFromAnalysisId,
        data: {
          rowMeta: {
            rowType: RowType.builderCohorts,
            rowIndex: 0,
          },
          shouldCohortImportToBraze: true,
          cohortBuilderInfo,
        },
        id: analysisId,
      }),
    );
  }, [query, dispatch, analysisId]);

  const items: IContextualMenuItem[] = [];

  const openSqlViewModal = () => {
    track(reportViewSql(metadata));
    dispatch(
      openModal({ type: ModalSliceType.ShowSql, data: null, id: analysisId }),
    );
  };

  if ((isDashboardInUrl || isWorkspacesInUrl) && !isTransientAnalysis) {
    items.push({
      key: 'rename-report',
      disabled: !entityPermissions.editAnalysis,
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="rename"
          label={'Rename Report'}
          onClick={openRenameAnalysisModal}
          isDisabled={!entityPermissions.editAnalysis}
          disabledReason={{
            content: entityPermissionsHint.editAnalysis,
          }}
        />
      ),
    });
  }

  if (
    chartType !== CHART_TYPES.COHORT_SAMPLE &&
    chartType !== CHART_TYPES.PREDICTION &&
    status !== AnalysisStatus.Failed &&
    !isTransientAnalysis &&
    (isDashboardInUrl || isWorkspacesInUrl)
  ) {
    items.push({
      key: 'save-changes',
      disabled: !entityPermissions.editAnalysis,
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="save-changes"
          label={'Save Changes'}
          onClick={saveChartSettings}
          isDisabled={!entityPermissions.editAnalysis}
          disabledReason={{
            content: entityPermissionsHint.editAnalysis,
          }}
        />
      ),
    });
  }

  const canRenderIncidents = chartTypesAllowIncidents.includes(chartType);
  if (
    status === AnalysisStatus.Success &&
    canRenderIncidents &&
    !singleDayLabel
  ) {
    items.push({
      key: 'Incidents',
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="incidents"
          label={'Incidents'}
          hasSubMenus={true}
        />
      ),
      subMenuProps: {
        styles: menuStyles,
        items: [
          {
            key: 'Incident Items',
            onRender: () => <IncidentTypeSwitches analysisId={analysisId} />,
          },
        ],
      },
    });
  }

  if (rebuildChart && !query.schema.disabled) {
    items.push({
      key: 'Rebuild',
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="refresh"
          label={'Rebuild'}
          onClick={rebuildChart}
        />
      ),
    });
  }

  if (showSql) {
    items.push({
      key: 'view-sql',
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="copy-sql"
          label={'View SQL'}
          onClick={openSqlViewModal}
        />
      ),
    });
  }

  if (
    isBrazeConnect &&
    chartType === CHART_TYPES.COHORT_SAMPLE &&
    'operation' in query &&
    query.operation?.key !== CohortSampleOperationKey.Compare &&
    !isEmbedded
  ) {
    items.push({
      key: 'Save and Export to Braze',
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="braze"
          label={'Save and Export to Braze'}
          onClick={handleSaveAndExportToBraze}
          isUnPermitted={!isPermittedToSaveCohortAndExportBraze}
        />
      ),
    });
  }

  if (
    chartType !== CHART_TYPES.COHORT_SAMPLE &&
    chartType !== CHART_TYPES.PREDICTION &&
    chartType !== CHART_TYPES.DATA_TABLE &&
    !isTransientAnalysis &&
    status !== AnalysisStatus.Failed &&
    !isEmbedded
  ) {
    items.push({
      key: 'Create New Schedule',
      disabled: !entityPermissions.createSchedule,
      onRenderContent: () => (
        <ContextualMenuOption
          iconName="schedule"
          label={'Create New Schedule'}
          onClick={openCreateAnalysisScheduleModal}
          isDisabled={!entityPermissions.createSchedule}
          disabledReason={{
            content: entityPermissionsHint.createSchedule,
          }}
        />
      ),
    });
  }

  if (isWorkspacesInUrl) {
    items.push(
      {
        key: 'Delete from Workspace Divider',
        itemType: ContextualMenuItemType.Divider,
      },
      {
        key: 'Delete from Workspace',
        onRenderContent: () => (
          <ContextualMenuOption
            iconName="trash"
            label={'Delete from Workspace'}
            onClick={openDeleteFromWorkspaceModal}
          />
        ),
      },
    );
  }

  if (isDashboardInUrl) {
    assertNotNil(onDelete);

    if (!query.schema.disabled) {
      items.push({
        key: 'duplicate-dashboard-report',
        disabled: !entityPermissions.duplicateAnalysisInContainer,
        onRenderContent: () => (
          <ContextualMenuOption
            iconName="duplicate"
            label={'Duplicate Report'}
            onClick={openDuplicateReportModal}
            isDisabled={!entityPermissions.duplicateAnalysisInContainer}
            disabledReason={{
              content: entityPermissionsHint.duplicateAnalysisInContainer,
            }}
          />
        ),
      });
    }
    items.push(
      {
        key: 'Delete from Dashboard Divider',
        itemType: ContextualMenuItemType.Divider,
      },
      {
        key: 'Delete from Dashboard',
        disabled: !entityPermissions.deleteAnalysisInContainer,
        onRenderContent: () => (
          <ContextualMenuOption
            iconName="trash"
            label={'Delete from Dashboard'}
            onClick={onDelete}
            isDisabled={!entityPermissions.deleteAnalysisInContainer}
            disabledReason={{
              content: entityPermissionsHint.deleteAnalysisInContainer,
            }}
          />
        ),
      },
    );
  }

  return (
    <KubitContextualMenu
      trigger={
        <IconContainer disabled={isDisabled}>
          <SvgIcon
            name="dots-three-outline-vertical"
            width={20}
            height={20}
            isDisabled={isDisabled}
            customFill="currentColor"
          />
        </IconContainer>
      }
      items={items}
      style={menuStyles}
      directionalHint={DirectionalHint.bottomRightEdge}
    />
  );
};
